import * as THREE from 'three';

import AbstractObject from '../../../misc/object';
import Arrow from '../../../objects/arrow';
import Line from '../../../objects/line';
import { HTMLEditableLabel } from '../../../objects/html-label';

import { concreteMaterial } from '../../../materials/ground';
import { unitTransform } from '@services/helper.service';
import { defaultUnits } from '@config';

import Scene from "@scene";
import { LabelsPadding } from '@scene/objects/ground';
import { APP_TEXTS } from '@texts';

const edgeSharpLevel = 50;
const minSharpLevel = 15;
const maxEdgeSize = 20;

export default class Ground extends AbstractObject {

  static types = {
    BASE: 'base',
    STAIRS: 'stairs',
    WALLS: 'walls',
    FLAT: 'flat',
    PEDESTAL: 'pedestal',
    FLOOR: 'floor'
  };

  constructor(options) {
    super(options);

    this.material = concreteMaterial;

    if (this.options.color) {
      this.material = concreteMaterial.clone();
      this.material.color = new THREE.Color(this.options.color);
    }

    switch (this.options.type) {
      case Ground.types.WALLS: {
        this.ground = this.getWallsObject();

        break;
      }

      case Ground.types.PEDESTAL: {
        this.ground = this.getPedestal();

        break;
      }

      case Ground.types.BASE:
      case Ground.types.STAIRS:
      case Ground.types.FLAT:
      case Ground.types.FLOOR:
      default: {
        const geometry = this.getGeometry();

        this.ground = new THREE.Mesh(geometry, this.material);

        break;
      }
    }

    this.object.addMesh(this.ground);

    this.prepareMesh();
  }

  get size() {
    switch (this.options.type) {
      case Ground.types.STAIRS: {
        return {
          width: this.options.stairs.width * this.options.stairs.count,
          height: this.options.stairs.height * this.options.stairs.count,
          depth: this.options.depth,
          tst: Math.sqrt(this.options.stairs.tst ** 2 + this.options.stairs.tst ** 2),
          step: {
            width: this.options.stairs.width,
            height: this.options.stairs.height
          }
        };
      }

      case Ground.types.WALLS: {
        return {
          width: this.options.walls.spaceBetween + this.options.walls.width,
          height: this.options.walls.height + this.options.walls.ground.height,
          depth: this.options.walls.depth + this.options.walls.ground.padding,
          wall: {
            width: this.options.walls.width,
            height: this.options.walls.height,
            depth: this.options.walls.depth,
            space: this.options.walls.spaceBetween
          },
          ground: {
            height: this.options.depth,
            padding: this.options.walls.ground.padding
          }
        };
      }

      case Ground.types.PEDESTAL: {
        const GROUND_WIDTH = this.options.pedestal.ground.padding + this.options.pedestal.pedestal.height;
        return {
          width: this.options.pedestal.width,
          height: GROUND_WIDTH,
          depth: this.options.pedestal.ground.depth + this.options.pedestal.pedestal.depth,
          ground: {
            padding: this.options.pedestal.ground.padding,
            width: GROUND_WIDTH,
            depth: this.options.pedestal.ground.depth
          },
          finish: {
            height: this.options.pedestal.finish.height,
            depth: this.options.pedestal.finish.depth
          },
          pedestal: {
            height: this.options.pedestal.pedestal.height,
            depth: this.options.pedestal.pedestal.depth
          }
        };
      }

      case Ground.types.BASE:
      case Ground.types.FLAT:
      case Ground.types.FLOOR:
      default: {
        return {
          width: this.options.width,
          height: this.options.height,
          depth: this.options.depth
        };
      }
    }
  }

  get randomSharpLevel() {
    return Math.random() * (edgeSharpLevel - minSharpLevel) + minSharpLevel;
  }

  prepareMesh() {
    switch (this.options.type) {
      case Ground.types.BASE:
      case Ground.types.FLOOR: {
        this.ground.position.x = this.size.width / -2;
        this.ground.position.y = this.size.height / -2;
        this.ground.position.z = this.size.depth / -2;

        break;
      }

      case Ground.types.STAIRS: {
        this.ground.rotation.y = Math.PI / -2;
        this.ground.rotation.z = Math.PI / -2;

        break;
      }

      default: {
        break;
      }
    }
  }

  getGeometry() {
    const shape = new THREE.Shape();

    switch (this.options.type) {
      // Added minimum size of 1 to prevent errors from extruding an invalid shape
      case Ground.types.FLAT: {
        shape.moveTo(0, 0);
        shape.lineTo(this.size.width || 1, 0);
        shape.lineTo(this.size.width || 1, this.size.height || 1);
        shape.lineTo(0, this.size.height || 1);
        shape.lineTo(0, 0);

        break;
      }

      case Ground.types.BASE: {
        shape.moveTo(0, 0);
        shape.lineTo(this.size.width, 0);
        for (let i = this.randomSharpLevel; i < this.size.height - this.randomSharpLevel; i += this.randomSharpLevel) {
          shape.lineTo(this.size.width - (Math.random() * maxEdgeSize), i);
        }
        shape.lineTo(this.size.width, this.size.height);
        shape.lineTo(0, this.size.height);
        shape.lineTo(0, 0);

        break;
      }

      // special case C1x = Infinity
      case Ground.types.FLOOR: {
        shape.moveTo(0, 0);
        shape.lineTo(this.size.width, 0);
        for (let i = this.randomSharpLevel; i < this.size.height - this.randomSharpLevel; i += this.randomSharpLevel) {
          shape.lineTo(this.size.width - (Math.random() * maxEdgeSize), i);
        }
        shape.lineTo(this.size.width, this.size.height);
        
        for (let i = this.size.height; i > this.randomSharpLevel; i -= this.randomSharpLevel) {
          shape.lineTo(-this.size.width + (Math.random() * maxEdgeSize), i);
        }
        shape.lineTo(-this.size.width, 0);
        shape.lineTo(0, 0);

        break;
      }

      case Ground.types.STAIRS: {
        const { width, height } = this.size.step;
        var { count } = this.options.stairs;

        if (count > 1) {
          let x = -width * count / 2;
          let y = -height * count / 2;

          shape.moveTo(x, y);

          y += this.size.tst + height;
          shape.lineTo(x, y);

          for (let i = 0; i <= (count -1) * 2; i++) { 
            x += i % 2 === 0 ? width : 0;
            y += i % 2 === 1 ? height : 0;

            shape.lineTo(x, y);
          }

          y -= this.size.tst;
          shape.lineTo(x, y);
        } else {
          let x = 0;
          let y = 0;

          shape.moveTo(x, y);
          y += this.size.tst + height;
          shape.lineTo(x, y);

          x += width;
          shape.lineTo(x, y);

          y -= this.size.tst;
          shape.lineTo(x, y);
        }

        break;
      }

      default: {
        throw new Error('Something went wrong!');
      }
    }

    const extrudeSettings = {
      depth: this.size.depth,
      bevelEnabled: false
    };

    return new THREE.ExtrudeGeometry(shape, extrudeSettings);
  }

  getWallsObject() {
    const object = new THREE.Object3D();

    // Create ground
    const ground = new Ground({
      type: Ground.types.FLAT,
      width: this.size.depth,
      height: this.size.wall.space + this.size.wall.width * 2,
      depth: this.size.ground.height
    });

    ground.mesh.position.x = -this.size.ground.padding;
    ground.mesh.position.y = (this.size.wall.space + (this.size.wall.width * 2)) / -2;
    ground.mesh.position.z = (this.size.wall.height / -2) - this.size.ground.height;

    object.add(ground.mesh);

    // Create left side
    const left = new Ground({
      type: Ground.types.BASE,
      width: this.size.wall.width,
      height: this.size.wall.height,
      depth: this.size.wall.depth
    });
    left.mesh.rotation.y = Math.PI / 2;
    left.mesh.rotation.x = Math.PI / 2;

    left.mesh.position.x = this.size.wall.depth / 2;
    left.mesh.position.y = (this.size.wall.space + this.size.wall.width) / 2;

    object.add(left.mesh);

    // Create right size
    const right = new Ground({
      type: Ground.types.BASE,
      width: this.size.wall.width,
      height: this.size.wall.height,
      depth: this.size.wall.depth
    });
    right.mesh.rotation.y = Math.PI / 2;
    right.mesh.rotation.x = Math.PI / -2;

    right.mesh.position.x = this.size.wall.depth / 2;
    right.mesh.position.y = (this.size.wall.space + this.size.wall.width) / -2;

    object.add(right.mesh);

    return object;
  }

  getPedestal() {
    const object = new THREE.Object3D();

    const GROUND_WIDTH = this.size.pedestal.height + this.size.ground.padding;

    const ground = new Ground({
      type: Ground.types.BASE,
      width: GROUND_WIDTH,
      height: this.size.width,
      depth: this.size.ground.depth,
      color: 0xdddddd
    });
    ground.position.z = ground.size.depth / 2;
    ground.frustumCulled = false;

    if (!this.options.removeFinish){
      object.add(ground.mesh);
    }

    this.pedestal = new Ground({
      type: Ground.types.FLAT,
      width: this.size.pedestal.height,
      height: this.size.width,
      depth: this.size.pedestal.depth
    });
    this.pedestal.position.y = this.size.width / -2;
    this.pedestal.position.x = GROUND_WIDTH / -2;
    this.pedestal.position.z = ground.position.z + ground.size.depth / 2;
    this.pedestal.frustumCulled = false;

    object.add(this.pedestal.mesh);

    this.finish = new Ground({
      type: Ground.types.FLAT,
      width: this.size.finish.height,
      height: this.size.width,
      depth: this.size.finish.depth,
      color: this.options.pedestal.finish.color
    });
    this.finish.position.z = ground.position.z + ground.size.depth / 2;
    this.finish.position.y = this.size.width / -2;
    this.finish.position.x = GROUND_WIDTH / -2 + this.pedestal.size.width;
    this.finish.frustumCulled = false;
  
    if (!this.options.removeFinish){
      object.add(this.finish.mesh);
    }

    return object;
  }

  getThicknessLabel(position, options, isBetweenWalls) {
    let thickness = isBetweenWalls ? this.size.ground.height : this.size.depth;
    var object = new THREE.Object3D();
    const arrow = new Arrow({
      size: thickness,
      // position: {
      //   x: position.z + this.size.height,
      //   y: position.y,
      //   z: -position.x - position.x / 15
      // },
      position,
      direction: Arrow.types.direction.Y,
      disableDepthTest: true
    });
    object.add(arrow.mesh);

    const topLine = new Line({
      from: {
        x: position.x,
        y: position.y,
        z: position.z
      },
      to: {
        x: position.x,
        y: position.y,
        z: position.z + LabelsPadding
      },
      disableDepthTest: true
    });
    object.add(topLine.mesh);

    const bottomLine = new Line({
      from: {
        x: position.x,
        y: position.y + thickness,
        z: position.z
      },
      to: {
        x: position.x,
        y: position.y + thickness,
        z: position.z + LabelsPadding
      },
      disableDepthTest: true
    });
    object.add(bottomLine.mesh);

    const htmlElement = new HTMLEditableLabel({
      text: ((!options || !options.is3DMode || options.showLabels) ? `${APP_TEXTS.BASE_PLATE_THICKNESS_LABEL.Content} = ` : '') + 
        `${unitTransform(+thickness).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(+thickness).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      minValue: 0
    });
    htmlElement.onInputChange((value, didChange) => {
      this.dispatchOptionUpdate({
        ground: {
          depth: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
        }
      },
      false,
      didChange);
    });

    if (options && options.is3DMode) {
      htmlElement.position.set(
        position.x,
        this.position.y,
        position.z
      );
      // if (!options.displayHorizontally) {
      //   htmlElement.align = HTMLEditableLabel.types.align.CENTER;
      //   htmlElement.position.z -= LabelsPadding;
      //   htmlElement.setDirection(
      //     htmlElement.position.clone().setY(this.position.y - this.size.depth / 2),
      //     htmlElement.position.clone().setY(this.position.y + this.size.depth / 2),
      //     false
      //   );
      // }
    } else {
      htmlElement.position.set(
        -position.z,
        position.y,
        0 //position.x
      );
    }

    return {
      arrow: object,
      htmlElement
    };
  }

  // only for Between-Walls
  createWLabel() {
    const position = {
      x: this.size.width / -2,
      y: this.size.height / -2 - LabelsPadding,
      z: 0
    };

    const object = new THREE.Object3D();
    const arrow = new Arrow({
      size: this.size.width,
      position,
      direction: Arrow.types.direction.X,
      disableDepthTest: true
    });
    object.add(arrow.mesh);
    
    const rightLine = new Line({
      from: {
        x: position.x,
        y: position.y,
        z: position.z
      },
      to: {
        x: position.x,
        y: position.y + LabelsPadding,
        z: position.z
      },
      disableDepthTest: true
    });
    object.add(rightLine.mesh);

    const leftLine = new Line({
      from: {
        x: position.x + this.size.width,
        y: position.y,
        z: position.z
      },
      to: {
        x: position.x + this.size.width,
        y: position.y + LabelsPadding,
        z: position.z
      },
      disableDepthTest: true
    });
    leftLine.disableDepthTest = true;
    object.add(leftLine.mesh);

    this.object.addMesh(object);

    const htmlElement = new HTMLEditableLabel({
      text: `W: ${unitTransform(+this.size.width).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(+this.size.width).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.BOTTOM,
      minValue: 0
    });
    htmlElement.onInputChange((value, didChange) => {
      this.dispatchOptionUpdate({
        ground: {
          walls: {
            depth: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
          }
        }
      },
      false,
      didChange);
    });
    htmlElement.position.set(
      position.z,
      position.x,
      position.y
    );

    return {
      arrow,
      htmlElement
    };
  }

  create3DWLabel(options) {
    const position = {
      x: -this.size.wall.depth,
      y: this.size.wall.height / 2 + LabelsPadding,
      z: -this.size.wall.space / 2
    };

    const object = new THREE.Object3D();
    const arrow = new Arrow({
      size: this.size.wall.depth,
      position,
      direction: Arrow.types.direction.X,
      disableDepthTest: true
    });
    object.add(arrow.mesh);
    
    const rightLine = new Line({
      from: {
        x: position.x,
        y: position.y,
        z: position.z
      },
      to: {
        x: position.x,
        y: position.y - LabelsPadding,
        z: position.z
      },
      disableDepthTest: true
    });
    object.add(rightLine.mesh);

    const leftLine = new Line({
      from: {
        x: position.x + this.size.wall.depth,
        y: position.y,
        z: position.z
      },
      to: {
        x: position.x + this.size.wall.depth,
        y: position.y - LabelsPadding,
        z: position.z
      },
      disableDepthTest: true
    });
    leftLine.disableDepthTest = true;
    object.add(leftLine.mesh);

    // this.object.addMesh(object);

    const htmlElement = new HTMLEditableLabel({
      text: (!options || !options.is3DMode || options.showLabels ? 'W = ' : '') + 
        `${unitTransform(+this.size.wall.depth).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(+this.size.wall.depth).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      minValue: 0
    });
    htmlElement.onInputChange((value, didChange) => {
      this.dispatchOptionUpdate({
        ground: {
          walls: {
            depth: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
          }
        }
      },
      false,
      didChange);
    });
    
    htmlElement.position.set(
      position.x + this.size.wall.depth / 2,
      position.y + 15,
      position.z
    );
    htmlElement.optimalCameraDist = 3000;
    htmlElement.zoomFactor = 0.5;
    if (!options.displayHorizontally) {
      htmlElement.setDirection(
        htmlElement.position.clone().setX(htmlElement.position.x + Math.max(25, this.size.wall.depth / 2)),
        htmlElement.position.clone().setX(htmlElement.position.x - Math.max(25, this.size.wall.depth / 2)),
        false
      );
    }

    return {
      object,
      htmlElement
    };
  }

  getDepthLabel(path) {
    if (!path) {
      const position = {
        x: this.position.x + this.size.width / 3,
        y: this.position.y - this.size.height / 3,
        z: this.position.z
      };

      path = [{ ...position }]; // eslint-disable-line

      position.x += this.size.width / 2;
      position.y -= this.size.height / 2;
      path.push({ ...position });

      position.y -= this.size.width / 2;
      path.push({ ...position });
    }

    const line = new Line({ path, disableDepthTest: true });

    this.object.addObject(line);

    const htmlElement = new HTMLEditableLabel({
      text: `${APP_TEXTS.BASE_PLATE_THICKNESS_LABEL.Content} = 
        ${unitTransform(+this.size.depth).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(+this.size.depth).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      verticalAlign: HTMLEditableLabel.types.verticalAlign.BOTTOM,
      minValue: 0
    });
    htmlElement.onInputChange((value, didChange) => {
      this.dispatchOptionUpdate({
        ground: {
          depth: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
        }
      },
      false,
      didChange);
    });
    htmlElement.position.set(
      -path[1].y,
      path[1].z,
      -path[1].x
    );

    return {
      htmlElement,
      path,
      line
    };
  }

  // Only for pedestal
  createHrefLabel(dispatch, options) {
    if (!this.finish) return null;

    // Z - up/down
    const arrow = new Arrow({
      position: {
        x: -this.finish.position.x - this.size.finish.height / 2,
        y: this.finish.position.z,
        z: -this.finish.position.y
      },
      size: this.size.finish.depth,
      direction: Arrow.types.direction.Y,
      disableDepthTest: true
    });
    const htmlElement = new HTMLEditableLabel({
      text: (options.showLabels ? 'Href: ' : '') + 
        `${unitTransform(+this.size.finish.depth).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(+this.size.finish.depth).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      verticalAlign: HTMLEditableLabel.types.align.CENTER,
      minValue: 0
    });
    htmlElement.onInputChange((value, didChange) => {
      dispatch({
        ground: {
          pedestal: {
            finish: {
              depth: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
            }
          }
        }
      },
      false,
      didChange);
    });
    htmlElement.position.set(
      arrow.position.x,
      arrow.position.y + this.size.finish.depth / 2,
      arrow.position.z
    );

    return {
      arrow,
      htmlElement
    };
  }

  // Only for pedestal
  createPedestalWidthLabel(dispatch, options, internalBasePlate) {
    if (!this.pedestal) return null;

    const object = new THREE.Object3D();
    const arrow = new Arrow({
      position: {
        x: -this.pedestal.position.x - this.size.pedestal.height,
        y: +this.pedestal.position.z + this.size.pedestal.depth,
        z: (this.pedestal.position.y - LabelsPadding) * (internalBasePlate ? -1 : 1)
      },
      size: this.size.pedestal.height,
      direction: Arrow.types.direction.X,
      disableDepthTest: true
    });
    object.add(arrow.mesh);
    
    const topLine = new Line({
      from: {
        x: arrow.position.x,
        y: arrow.position.y,
        z: arrow.position.z
      },
      to: {
        x: arrow.position.x ,
        y: arrow.position.y,
        z: arrow.position.z + (internalBasePlate ? -LabelsPadding : LabelsPadding)
      }
    });
    object.add(topLine.mesh);

    const bottomLine = new Line({
      from: {
        x: arrow.position.x + this.size.pedestal.height,
        y: arrow.position.y,
        z: arrow.position.z
      },
      to: {
        x: arrow.position.x + this.size.pedestal.height,
        y: arrow.position.y,
        z: arrow.position.z + (internalBasePlate ? -LabelsPadding : LabelsPadding)
      }
    });
    object.add(bottomLine.mesh);

    const htmlElement = new HTMLEditableLabel({
      text: (options.showLabels ? 'W: ' : '') + 
        `${unitTransform(+this.size.pedestal.height).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(+this.size.pedestal.height).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: HTMLEditableLabel.types.align.CENTER,
      minValue: 0
    });
    
    if (!options.displayHorizontally)
      htmlElement.setDirection(arrow.position, arrow.endPosition);
      
    htmlElement.onInputChange((value, didChange) => {
      dispatch({
        ground: {
          pedestal: {
            pedestal: {
              height: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
            }
          }
        }
      },
      false,
      didChange);
    });
    htmlElement.position.set(
      arrow.position.x + this.size.pedestal.height / 2,
      arrow.position.y + LabelsPadding,
      arrow.position.z - (internalBasePlate ? -LabelsPadding : LabelsPadding)
    );

    return {
      arrow: object,
      htmlElement
    };
  }

  // Only for stairs
  createHstLabel(dispatch, options) {
    const isOdd = this.options.stairs.count % 2;

    const position = new THREE.Vector3(
      this.position.z + this.size.depth,
      this.position.x + this.size.tst - (isOdd ? this.size.step.height / 2 : 0),
      this.position.y + (isOdd ? this.size.step.width / 2 : 0)
    );

    const arrow = new Arrow({
      position,
      size: this.size.step.height,
      direction: Arrow.types.direction.Y,
      disableDepthTest: true
    });
    const htmlElement = new HTMLEditableLabel({
      text: (options.showLabels ? 'Hst: ' : '') + 
        `${unitTransform(+this.size.step.height).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(+this.size.step.height).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      minValue: 1
    });
    htmlElement.onInputChange((value, didChange) => {
      dispatch({
        ground: {
          stairs: {
            height: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
          }
        }
      },
      false,
      didChange);
    });
    htmlElement.position.set(
      position.x,
      position.y + this.size.step.height / 2,
      position.z
    );

    return {
      arrow,
      htmlElement
    };
  }

  // Only for stairs
  createLstLabel(dispatch, options) {
    const isOdd = this.options.stairs.count % 2;

    const position = new THREE.Vector3(
        this.position.z + this.size.depth,
        this.position.x + this.size.tst - (isOdd ? this.size.step.height / 2 : 0),
        this.position.y + (isOdd ? this.size.step.width / 2 : 0)
    );

    const arrow = new Arrow({
      position,
      size: this.size.step.width,
      direction: Arrow.types.direction.Z,
      disableDepthTest: true
    });
    const htmlElement = new HTMLEditableLabel({
      text: (options.showLabels ? 'Lst: ' : '') + 
      `${unitTransform(+this.size.step.width).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(+this.size.step.width).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      minValue: 1
    });
    htmlElement.onInputChange((value, didChange) => {
      dispatch({
        ground: {
          stairs: {
            width: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
          }
        }
      },
      false,
      didChange);
    });
    htmlElement.position.set(
      position.x,
      position.y,
      position.z + this.size.step.width / 2
    );

    return {
      arrow,
      htmlElement
    };
  }

  // Only for stairs
  createTstLabel(dispatch, options) {
    const isOdd = this.options.stairs.count % 2;
    const position = new THREE.Vector3(
      this.position.z + this.size.depth, 
      this.position.x + this.size.tst - (isOdd ? this.size.step.height / 2 : 0),
      this.position.y + (isOdd ? this.size.step.width / 2 : 0)
    );

    // const arrow = new Arrow({
    //   position,
    //   size: this.options.stairs.tst,
    //   direction: Arrow.types.direction.Y,
    //   disableDepthTest: true
    // });
    
    const arrow = new THREE.Object3D();
    const angle = Math.atan2(this.options.stairs.height, this.options.stairs.width);
    const dir = new THREE.Vector3(0, -Math.cos(angle), -Math.sin(angle)).normalize();
    const arrowSize = this.options.stairs.tst;
    const arrowHelper = new THREE.ArrowHelper(
      dir,
      position, 
      arrowSize, 
      0x000000, 
      10, 
      5
    );

    arrow.add(arrowHelper);
    Array.prototype.forEach.call(arrowHelper.children, (item) => {
      item.material.depthTest = false;
    });
    // arrow.mesh.rotateOnWorldAxis(new THREE.Vector3(1, 0, 0), Math.atan2(this.options.stairs.height, this.options.stairs.width));

    const htmlElement = new HTMLEditableLabel({
      text: (options.showLabels ? 'Tst: ' : '') + 
        `${unitTransform(+this.options.stairs.tst).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(+this.options.stairs.tst).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      minValue: 1
    });
    htmlElement.onInputChange((value, didChange) => {
      dispatch({
        ground: {
          stairs: {
            tst: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
          }
        }
      },
      false,
      didChange);
    });
    let middlePoint = position.clone().addScaledVector(
      dir, 
      this.options.stairs.tst / 2
    );
    let paddingDir = dir.clone().applyAxisAngle(new THREE.Vector3(1, 0, 0), Math.PI / 2);
    middlePoint.addScaledVector(paddingDir, 15); 
    htmlElement.position.set(
      middlePoint.x,
      middlePoint.y,
      middlePoint.z,
    );

    htmlElement.setDirection(
      htmlElement.position.clone().addScaledVector(dir, -Math.max(25, this.options.stairs.tst / 2)),
      htmlElement.position.clone().addScaledVector(dir, Math.max(25, this.options.stairs.tst / 2)),
      false
    );

    return {
      arrow,
      htmlElement
    };
  }

}




// WEBPACK FOOTER //
// ./src/object-view/generators/safety-barrier/objects/ground.js